import { useEffect, useState } from 'react';
import { Box, Flex, Grid } from 'components/box';
import { Text } from 'components/typography';
import Button from 'components/button';
import { ProductInterface } from 'types/types';
import {
  Facebook as FacebookIcon,
  Share as IosShareIcon,
  Share2 as ShareIcon,
} from 'react-feather';
import MailIcon from './mail.svg';
import WhatsappIcon from './whatsapp.svg';
import dynamic from 'next/dynamic';
import { ClientOnly } from 'components/_shared/widgets/render-control';
import { useIsMobile } from 'hooks/use-media';
import { css, Theme } from '@emotion/react';
import { isBrowserWindowAvailable } from '../../../../util/is-browser-window-available';
import Tooltip from 'components/tooltip';
import { Link } from 'components/configurable-routing';

const SocialSharingModal = dynamic(
  () => import('components/_pages/products/modals/social-sharing')
);

const getMobileDetect = (userAgent: NavigatorID['userAgent']) => ({
  isIos: Boolean(userAgent.match(/iPhone|iPad|iPod/i)),
});

const useMobileDetect = () => {
  const [device, setDevice] = useState({ isIos: false });

  useEffect(() => {
    if (typeof navigator !== 'undefined') {
      setDevice(getMobileDetect(navigator.userAgent));
    }
  }, []);

  return device;
};

const socialIconStyles = (theme: Theme) =>
  css`
    background-color: ${theme.colors.lightGrey};
    padding: 2px;
    border-radius: 100%;
    width: 34px;
    height: 34px;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    transition: all 0.2s ease;

    span {
      color: ${theme.colors.darkGrey};
    }

    &:hover {
      background-color: ${theme.colors.mediumGrey};
    }

    @media ${theme.mediaQueries.mobileOnly} {
      width: 50px;
      height: 50px;
    }

    & :first-of-type {
      margin-left: 0 !important;
    }
  `;

const SocialShareButton = ({ onClick }: { onClick: () => void }) => {
  const currentDevice = useMobileDetect();
  return (
    <Button
      variant="flat"
      onClick={onClick}
      mb={0}
      p={0}
      css={theme => css`
        text-transform: none;

        &:hover {
          color: ${theme.colors.primary};
          svg {
            stroke: ${theme.colors.primary};
          }
        }
      `}
    >
      <Flex
        justifyContent={['flex-end', null, 'flex-start']}
        alignItems="center"
      >
        {currentDevice.isIos && <IosShareIcon size={20} color="black" />}
        {!currentDevice.isIos && <ShareIcon size={20} color="black" />}
      </Flex>
    </Button>
  );
};

const ShareLink = ({
  href,
  title,
  icon,
  label,
  id,
}: {
  href: string;
  title: string;
  icon: React.ReactNode;
  label?: string;
  id?: string;
}) => (
  <Link
    href={href}
    title={title}
    target="_blank"
    prefetch={false}
    rel="noopener noreferrer"
    id={id}
  >
    <Flex
      alignItems="center"
      css={theme => css`
        gap: ${theme.space[2]}px;
      `}
    >
      <Box css={socialIconStyles}>{icon}</Box>
      <Text fontSize={2} fontWeight="bold" color="darkGrey">
        {label ?? title}
      </Text>
    </Flex>
  </Link>
);

const SharingActions = ({
  product,
  isMobile,
}: {
  product: ProductInterface;
  isMobile: boolean;
}) => {
  const [baseDomain, setBaseDomain] = useState('');
  const [isSocialModalOpen, setIsSocialModalOpen] = useState(false);

  const url = encodeURIComponent(`${baseDomain}`);

  // explicitly encode all URI components before the URL is built
  const encodedProductName = encodeURIComponent(product.name);

  const whatsappMessage =
    `Hey!` +
    `%0A` +
    `%0AI know how much you love cool stuff! I saw this on OneDayOnly and I had to share it with you. ` +
    `It%27s kind of a big deal:` +
    `%0A` +
    `%0A${encodedProductName}` +
    `%0A` +
    `%0AGo on, have a look:` +
    `%0A${url}?utm_source=whatsapp&utm_medium=share`;

  const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${baseDomain}&utm_source=meta&utm_medium=share`;
  const mailUrl =
    `mailTo:?subject=Here%27s the deal! Check out this amazing product on OneDayOnly&body=Hey!` +
    `%0A` +
    `%0AI know you%27ve received too many emails today, but I also know how much you love cool stuff!` +
    `%0A` +
    `%0AI saw this on OneDayOnly and I had to share it with you.` +
    `%0A` +
    `%0AIt%27s kind of a big deal:` +
    `%0A` +
    `%0A${encodedProductName}` +
    `%0A` +
    `%0A` +
    `%0AGo on, have a look:` +
    `%0A${url}?utm_source=email&utm_medium=share`;

  const whatsappUrl = isMobile
    ? `whatsapp://send?text=${whatsappMessage}`
    : `https://wa.me?text=${whatsappMessage}`;

  useEffect(() => {
    isBrowserWindowAvailable() && setBaseDomain(window.location.href);
  }, []);

  return (
    <>
      {isMobile ? (
        <Flex>
          <div css={socialIconStyles}>
            <SocialShareButton onClick={() => setIsSocialModalOpen(true)} />
          </div>
        </Flex>
      ) : (
        <Flex
          mt={2}
          flexDirection="row"
          css={css`
            gap: 1em;
          `}
        >
          <div css={socialIconStyles}>
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              id="WhatsappSocialSharing"
            >
              <Tooltip content="Share on Whatsapp" placement="top">
                <WhatsappIcon size={18} />
              </Tooltip>
            </a>
          </div>
          <div css={socialIconStyles}>
            <a
              href={mailUrl}
              target="_blank"
              rel="noopener noreferrer"
              id="EmailSocialSharing"
            >
              <Tooltip content="Share via email" placement="top">
                <MailIcon size={10} />
              </Tooltip>
            </a>
          </div>
          <div css={socialIconStyles}>
            <a
              href={facebookUrl}
              id="MetaSocialSharing"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Tooltip content="Share on Facebook" placement="top">
                <FacebookIcon stroke="none" fill="#747e86" size={20} />
              </Tooltip>
            </a>
          </div>
        </Flex>
      )}

      <SocialSharingModal
        close={() => setIsSocialModalOpen(false)}
        isOpen={isSocialModalOpen}
      >
        <Grid>
          <ShareLink
            href={whatsappUrl}
            title="Whatsapp"
            icon={<WhatsappIcon size={30} />}
            id="WhatsappSocialSharing"
          />
          <ShareLink
            href={mailUrl}
            title="Mail"
            icon={<MailIcon size={30} />}
            label="Email"
            id="EmailSocialSharing"
          />
          <ShareLink
            href={facebookUrl}
            title="Facebook"
            icon={<FacebookIcon stroke="none" fill="#747e86" size={30} />}
            id="MetaSocialSharing"
          />
        </Grid>
      </SocialSharingModal>
    </>
  );
};

const Wrapper = ({ product }: { product: ProductInterface }) => {
  const isMobile = useIsMobile();

  return (
    <ClientOnly>
      <SharingActions product={product} isMobile={isMobile} />
    </ClientOnly>
  );
};

export default Wrapper;
